import { useCallback, useEffect, useRef, useState } from "react";
import { toggleDesignerMode } from "@/hooks/useDesignerMode";
import { useNavigate } from "react-router-dom";
import { BookOpen, Globe, RotateCcw, Users } from "lucide-react";
import Rulebook from "@/components/game/Rulebook";
import FeedbackButton from "@/components/FeedbackButton";
import { clearOnline, getLobby, getOnline, saveOnline } from "@/lib/api";
import { ARTWORKS } from "@/components/home/homeShared";
import Gallery from "@/components/home/designs/Gallery";
import "@/components/home/home.css";

// "Back to your lobby / game": only when the saved ticket is still a seat. Invalid tickets are cleared.
function useResume() {
  const [resume, setResume] = useState(null);
  useEffect(() => {
    const saved = getOnline();
    if (!saved) return undefined;
    let alive = true;
    getLobby(saved.code)
      .then((l) => {
        if (!alive) return;
        if (l.my_seat === null || l.my_seat === undefined) return clearOnline();
        if (l.status === "waiting") return setResume({ kind: "lobby", to: `/lobby/${l.code}` });
        if (!l.game_exists) return clearOnline();
        saveOnline({ ...saved, game_id: l.game_id });
        setResume({ kind: "game", to: `/game/${l.game_id}` });
      })
      .catch((e) => {
        if (alive && e?.response?.status === 404) clearOnline();
      });
    return () => {
      alive = false;
    };
  }, []);
  return resume;
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const resume = useResume();
  const [showRulebook, setShowRulebook] = useState(false);
  const closeRulebook = useCallback(() => setShowRulebook(false), []);
  const titleClicks = useRef([]);
  const onTitleClick = () => {
    const now = Date.now();
    titleClicks.current = [...titleClicks.current.filter((t) => now - t < 3000), now];
    if (titleClicks.current.length >= 5) {
      titleClicks.current = [];
      toggleDesignerMode();
    }
  };

  const actions = [
    resume && { key: "resume", tier: "resume", testid: `home-back-to-${resume.kind}-btn`, label: resume.kind === "game" ? "Back to your game" : "Back to your lobby", hint: "Pick up where you left off", Icon: RotateCcw, onClick: () => navigate(resume.to) },
    { key: "local", tier: "primary", testid: "home-pass-and-play-btn", label: "Pass & Play", hint: "One screen, passed around", Icon: Users, onClick: () => navigate("/local") },
    { key: "online", tier: "primary", testid: "home-play-online-btn", label: "Play Online", hint: "Each on your own device", Icon: Globe, onClick: () => navigate("/online") },
    { key: "rules", tier: "secondary", testid: "home-rulebook-btn", label: "Rulebook", hint: "The full rules", Icon: BookOpen, onClick: () => setShowRulebook(true) },
  ].filter(Boolean);

  return (
    <div data-testid="home-screen" className="relative w-full h-screen overflow-hidden">
      <FeedbackButton className="btn-ghost absolute top-3 right-3 z-10" style={{ height: 38, background: "rgba(253, 251, 243, 0.85)", border: "1px solid #E9E3D3" }} />
      <Gallery art={ARTWORKS.droplet} actions={actions} showBlurb onTitleClick={onTitleClick} />
      <Rulebook open={showRulebook} onClose={closeRulebook} />
    </div>
  );
}
